import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class RaceContestantTrackingUpdateRequest extends BaseObj {
    public ContestantId: number | null;
    public __contestantid: number | null;

    public IgnoreContestantInTracking: boolean | null;
    public __ignorecontestantintracking: boolean | null;

    public Meters: number | null;
    public __meters: number | null;

    public RaceContenstantId: number | null;
    public __racecontenstantid: number | null;

    public Speed: number | null;
    public __speed: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ContestantId?: number | null;
        EntityState?: EntityState;
        IgnoreContestantInTracking?: boolean | null;
        Meters?: number | null;
        RaceContenstantId?: number | null;
        Speed?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<RaceContestantTrackingUpdateRequest>(
            unref(this.__entityState),
            (states) => {
                if (isRef(this.__entityState)) {
                    this.__entityState.value = states.EntityStateWithChildrenConsidered;
                } else {
                    console.warn(
                        'RaceContestantTrackingUpdateRequest tried to set entitystate, but entitystate is not a ref! This should never happen',
                    );
                }
                this.__selfEntityState = states.SelfEntityState;
            },
        );
        this.ContestantId = values.ContestantId ?? null;
        this.__contestantid = values.ContestantId ?? null;
        Object.defineProperty(this, 'ContestantId', {
            get(): number | null {
                return this.__contestantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ContestantId', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IgnoreContestantInTracking = values.IgnoreContestantInTracking ?? null;
        this.__ignorecontestantintracking = values.IgnoreContestantInTracking ?? null;
        Object.defineProperty(this, 'IgnoreContestantInTracking', {
            get(): boolean | null {
                return this.__ignorecontestantintracking;
            },
            set(value: boolean | null) {
                this.__entityStateHandler.propertyChanged('IgnoreContestantInTracking', this.__ignorecontestantintracking, value);
                this.__ignorecontestantintracking = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Meters = values.Meters ?? null;
        this.__meters = values.Meters ?? null;
        Object.defineProperty(this, 'Meters', {
            get(): number | null {
                return this.__meters;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Meters', this.__meters, value);
                this.__meters = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContenstantId = values.RaceContenstantId ?? null;
        this.__racecontenstantid = values.RaceContenstantId ?? null;
        Object.defineProperty(this, 'RaceContenstantId', {
            get(): number | null {
                return this.__racecontenstantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RaceContenstantId', this.__racecontenstantid, value);
                this.__racecontenstantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Speed = values.Speed ?? null;
        this.__speed = values.Speed ?? null;
        Object.defineProperty(this, 'Speed', {
            get(): number | null {
                return this.__speed;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Speed', this.__speed, value);
                this.__speed = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ContestantId": ${JSON.stringify(this.ContestantId)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"IgnoreContestantInTracking": ${JSON.stringify(this.IgnoreContestantInTracking)}` +
            ',' +
            `"Meters": ${JSON.stringify(this.Meters)}` +
            ',' +
            `"RaceContenstantId": ${JSON.stringify(this.RaceContenstantId)}` +
            ',' +
            `"Speed": ${JSON.stringify(this.Speed)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of RaceContestantTrackingUpdateRequest from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ContestantId?: number | null;
        IgnoreContestantInTracking?: boolean | null;
        Meters?: number | null;
        RaceContenstantId?: number | null;
        Speed?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): RaceContestantTrackingUpdateRequest {
        return new RaceContestantTrackingUpdateRequest(values);
    }

    public static _FromServerJson(json: any): RaceContestantTrackingUpdateRequest {
        return new RaceContestantTrackingUpdateRequest({
            ContestantId: json.ContestantId ?? null,
            IgnoreContestantInTracking: json.IgnoreContestantInTracking ?? null,
            Meters: json.Meters ?? null,
            RaceContenstantId: json.RaceContenstantId ?? null,
            Speed: json.Speed ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of RaceContestantTrackingUpdateRequest from another instance of RaceContestantTrackingUpdateRequest.
     * @param other The RaceContestantTrackingUpdateRequest you want to clone
     * @returns A cloned RaceContestantTrackingUpdateRequest
     */
    public static FromOther(other: RaceContestantTrackingUpdateRequest): RaceContestantTrackingUpdateRequest {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return RaceContestantTrackingUpdateRequest._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RaceContestantTrackingUpdateRequest.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ContestantId' | 'IgnoreContestantInTracking' | 'Meters' | 'RaceContenstantId' | 'Speed')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'RaceContestantTrackingUpdateRequest';
}
